import { useCallback, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'react-use'

import { useUpdateFeedbackPostMutation } from '~generated/hooks'
import { FeedbackPostFragment } from '~generated/operations'
import { UpdateFeedbackPostInput } from '~generated/types'

function toUpdateInput(data: FeedbackPostFragment): UpdateFeedbackPostInput {
  return {
    id: data.id,
    title: data.title,
    description: data.description,
    internal: data.internal,
    status: data.status,
    dealIds: data.deals?.map(({ id }) => id),
  }
}

// TODO - encapsulate reusable parts of the hook for other entities
export default function useUpdateFeedbackPost(feedbackPost: FeedbackPostFragment) {
  const updatedAt = useRef(feedbackPost?.updatedAt)
  const [data, setData] = useState(feedbackPost)
  const [updateFeedbackPostMutation, options] = useUpdateFeedbackPostMutation()

  // Updates local state if cache was changed in some other place of the application
  useEffect(() => {
    if (feedbackPost && (!updatedAt.current || feedbackPost?.updatedAt > updatedAt.current)) {
      setData((prev) => ({
        ...prev,
        ...feedbackPost,
      }))

      updatedAt.current = feedbackPost.updatedAt
    }
  }, [feedbackPost])

  useDebounce(
    () => {
      if (updatedAt.current > feedbackPost?.updatedAt) {
        updateFeedbackPostMutation({
          variables: {
            input: toUpdateInput(data),
          },
          update() {
            // Prevents redundant rerender after cache update
            updatedAt.current = Date.now()
          },
        })
      }
    },
    500,
    [data]
  )

  const update = useCallback((nextState: Partial<FeedbackPostFragment>) => {
    updatedAt.current = Date.now()

    setData((prevState) => {
      return {
        ...prevState,
        ...nextState,
        updatedAt: updatedAt.current,
      }
    })
  }, [])

  return [update, { ...options, data }] as const
}
